import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, AlertTriangle } from "lucide-react";

// Every row carries the patient's name and its own progress, so the list means
// something without opening anything. A list whose rows must each be opened to
// be understood is a list nobody uses.
export default function PhysioCaseload() {
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState("active");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/physio/plans?status=${status}&limit=500`)
      .then(({ data }) => setPlans(data.plans || []))
      .catch((e) => toast.error(formatErr(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [status]);

  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = t
      ? plans.filter((p) => (p.patient_name || "").toLowerCase().includes(t) ||
                            (p.pt_diagnosis || "").toLowerCase().includes(t))
      : plans;
    // Whoever needs attention first.
    return [...list].sort((a, b) =>
      (b.progress?.behind_schedule ? 1 : 0) - (a.progress?.behind_schedule ? 1 : 0));
  }, [plans, q]);

  const TABS = [
    ["active", "Active"],
    ["discharged", "Discharged"],
    ["completed", "Completed"],
    ["", "All"],
  ];

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="physio-caseload">
      <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Work Sans" }}>
        Caseload
      </h1>

      <div className="flex flex-wrap gap-2 items-center">
        {TABS.map(([k, l]) => (
          <Button key={k || "all"} size="sm" variant={status === k ? "default" : "outline"}
                  onClick={() => setStatus(k)}>{l}</Button>
        ))}
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Filter by patient or diagnosis…"
                 value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-6 text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-slate-500">No plans of care match.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left font-semibold px-4 py-2">Patient</th>
                  <th className="text-left font-semibold px-4 py-2">Diagnosis</th>
                  <th className="text-left font-semibold px-4 py-2">Therapist</th>
                  <th className="text-left font-semibold px-4 py-2">Plan</th>
                  <th className="text-right font-semibold px-4 py-2">Sessions</th>
                  <th className="text-right font-semibold px-4 py-2">Attendance</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const pr = p.progress || {};
                  return (
                    <tr key={p.id} className="border-t hover:bg-slate-50">
                      <td className="px-4 py-2 font-medium text-slate-900 whitespace-nowrap">
                        {p.patient_name || "—"}
                        {pr.behind_schedule && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-800">
                            <AlertTriangle className="h-3.5 w-3.5" />behind
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">{p.pt_diagnosis}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-slate-600">{p.therapist_name}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-slate-600">
                        {p.frequency_per_week}×/wk · {p.duration_weeks}wk
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">
                        {pr.completed} / {pr.authorized_sessions}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {pr.attendance_rate_pct != null ? `${pr.attendance_rate_pct}%` : "—"}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button size="sm" variant="outline"
                                onClick={() => nav(`/physio/plans/${p.id}`)}>Open</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
